/**
 * Pure progress-line logic for the agent application-detail screen.
 * Kept Angular-free so it can be unit-tested in isolation.
 *
 * FLOW-402 (DOC-89-A §5): the four-step journey this once built on screen
 * (*Started → Submitted → Under review → Active*) is retired — the stage row
 * is the back end's (`stage_row`), drawn by `app-stage-row`.
 */

import { KsOutstandingItem, KsPosition } from '../../../core/kickstart/kickstart.types';
import { progressLine } from '../../entity/kickstart/detail/outstanding/progress-line';

/**
 * FLOW-344 (DOC-73 UI-DA-73-03) — the agent's line on a Kickstart application,
 * in the client page's words, by the position the back end reports. No position
 * (outside the window, or a Debtors Finance application) → the stage's copy.
 * 🔒 Never "nothing you need to do" while the business owes an item.
 */
export function agentProgressLine(
  stage: string,
  facilityStatus: string,
  position: KsPosition | null,
  items: readonly KsOutstandingItem[],
): string {
  if (!position) return nextStepCopy(stage, facilityStatus);
  const businessOwes = items.some((i) => i.owner === 'BUSINESS');
  const shown = position === 'CAPX_WORKING' && businessOwes ? 'BUSINESS_NEEDED' : position;
  return progressLine(shown, items) ?? nextStepCopy(stage, facilityStatus);
}

/** One sentence of plain-language guidance for the agent, by stage alone. */
export function nextStepCopy(stage: string, facilityStatus: string): string {
  if (facilityStatus === 'ACTIVE') {
    return 'The facility is live and active.';
  }
  switch (stage) {
    case 'DRAFT':
      return 'This application is still a draft — your client needs to complete and submit it.';
    case 'SUBMITTED':
      return 'Submitted and waiting for the CapX team to review. No action needed from you right now.';
    case 'MORE_INFO_REQUIRED':
      return 'CapX has asked for more information — your client needs to respond before the review can continue.';
    case 'DECLINED':
      return 'This application was declined.';
    default:
      return '';
  }
}
